import React from 'react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '@mobi/component-library/Base/Buttons'
import { List, ListItem } from '@mobi/component-library/Base/List'
import { StatusBadge } from '@mobi/component-library/Feedback/StatusBadge'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { Icon as IconLegacy, type IconType } from '@mobi/component-library/Legacy/Icon'
import { Flex } from '@mobi/component-library/Misc/Flex'
import { Checkbox } from '@mobi/component-library/Selection&Input/Inputs'
import { InfoHeader } from '@mobi/component-library/Typography/InfoHeader'
import { hexColors } from '@mobi/settings/hexColors'
import { setSelectedTracks } from '@core/Areas/StructuredSearch/Store'
import { selectSearchCriteria } from '@core/Areas/StructuredSearch/Store/selectors'
import type { StructuredSearchTrack } from '@core/Areas/StructuredSearch/types'
import { shouldApplySafeAreaBottomPadding } from '@core/Settings/cssConditions'

interface IconTypes {
  Races: IconType
  Trots: IconType
  Dogs: IconType
}

export const IconTypes: IconTypes = { Races: 'races', Trots: 'trot', Dogs: 'dog' }

export const SelectTracks = React.memo<{
  availableTracks: StructuredSearchTrack[] | undefined
  onDismiss(): void
}>(
  ({ availableTracks, onDismiss }) => {
    const dispatch = useDispatch()
    const searchCriteria = useSelector(selectSearchCriteria)

    const [selectedModalTrackIds, setSelectedModalTrackIds] = React.useState<string[]>(() =>
      searchCriteria.selectedTracks ? searchCriteria.selectedTracks : []
    )

    const handleClearButtonClick = () => setSelectedModalTrackIds([])

    const handleCloseButtonClick = (shouldSave: boolean) => {
      if (shouldSave) dispatch(setSelectedTracks(availableTracks ? selectedModalTrackIds : []))
      onDismiss()
    }

    const selectCheckBox = React.useCallback((id: string) => {
      setSelectedModalTrackIds(trackIds =>
        trackIds.includes(id) ? trackIds.filter(trackId => trackId !== id) : [...trackIds, id]
      )
    }, [])

    return (
      <>
        <SearchOverlayStyled>
          {availableTracks && availableTracks.length > 0 ? (
            <List shouldHaveBottomBorder={false} testId='SmartForm.SelectTracks.List'>
              {availableTracks.map(track => {
                const iconType = IconTypes[track.contestType as keyof typeof IconTypes]
                return (
                  <ListItem
                    key={track.id}
                    onClick={() => selectCheckBox(track.id)}
                    content={
                      <Flex alignItems='center' gap={spacing.smx2}>
                        {track.name}

                        {track.isDuplicate ? (
                          <IconLegacy
                            size='2.5rem'
                            type={iconType}
                            color={hexColors.darkGrey}
                            data-tid-structured-search-track-icon={track.id}
                          />
                        ) : null}
                      </Flex>
                    }
                    contentRight={
                      <>
                        <StatusBadge color='gray'>{track.location}</StatusBadge>

                        <span style={{ pointerEvents: 'none' }}>
                          <Checkbox
                            name={track.location}
                            id={track.id}
                            data-tid-structured-search-track-checkbox={track.id}
                            checked={selectedModalTrackIds.includes(track.id)}
                            size={2.2}
                            onChange={() => null}
                          />
                        </span>
                      </>
                    }
                  />
                )
              })}
            </List>
          ) : (
            <div style={{ paddingTop: spacing.lg }}>
              <InfoHeader
                size='large'
                icon='warning'
                header='No Tracks Found'
                subHeader='Try making your criteria less specific'
              />
            </div>
          )}
        </SearchOverlayStyled>

        {availableTracks && availableTracks.length > 0 && (
          <div className='track-select__actions'>
            <Flex justifyContent='space-between' gap={spacing.smx1}>
              <Button
                isFullWidth
                color='secondary_grey'
                onClick={handleClearButtonClick}
                data-testid='Criteria.SelectTracks.Clear'
              >
                Clear
              </Button>

              <Button
                isFullWidth
                color='primary'
                onClick={() => handleCloseButtonClick(true)}
                data-testid='Criteria.SelectTracks.Done'
              >
                Done
              </Button>
            </Flex>
          </div>
        )}
      </>
    )
  },
  () => true
)
SelectTracks.displayName = 'SelectTracks'

// ======
// Styles
// ======

const SearchOverlayStyled = styled.div({
  flex: 1,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  paddingBottom: '6.5rem',

  '> .track-select__header': {
    display: 'flex',
    boxSizing: 'border-box',
    padding: `${spacing.sm} ${spacing.sm} ${spacing.sm} 0`,
    fontFamily: font.family.primary,

    // title
    '> h2': {
      display: 'flex',
      alignItems: 'center',
      fontWeight: font.weight.semibold,
      fontSize: font.size.xl2.fontSize,
      lineHeight: font.size.xl2.lineHeight,
      letterSpacing: font.size.xl2.letterSpacing,
      color: colors.black,
      textTransform: 'unset',
      textShadow: 'none',

      // chevron
      '> button': {
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        padding: 0,
        border: 0,
        background: 'transparent',
        minWidth: '5rem',
      },
    },
  },

  '& + .track-select__actions': {
    boxSizing: 'border-box',
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    minHeight: '6rem',
    padding: '1rem',
    paddingBottom: shouldApplySafeAreaBottomPadding ? 'var(--safe-area-inset-bottom, 0)' : 0,
  },
})
